/* eslint-disable react/no-array-index-key */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import Helmet from 'react-helmet';
import { withUrlGenerator } from '@folklore/react-app';
import { TweenMax, TimelineMax } from 'gsap';
import 'whatwg-fetch';

import ProductionCard from '../cards/Production';
import Menu from '../partials/Menu';
import PulseLoader from '../partials/PulseLoader';

import { setProductions as setProductionsActions } from '../../actions/SiteActions';

import styles from '../../../styles/pages/productions.scss';

const propTypes = {
    intl: PropTypes.shape({
        formatMessage: PropTypes.func,
    }).isRequired,
    page: PropTypes.shape({
        data: PropTypes.shape({
            blocks: PropTypes.array,
        }),
    }).isRequired,
    urlGenerator: PropTypes.shape({
        route: PropTypes.func,
    }).isRequired,
    productions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
    })),
    categories: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.objectOf(PropTypes.string),
    })).isRequired,
    category: PropTypes.string,
    setProductions: PropTypes.func.isRequired,
};

const defaultProps = {
    productions: null,
    category: null,
};

class Productions extends PureComponent {
    constructor(props) {
        super(props);

        this.onProductionsLoaded = this.onProductionsLoaded.bind(this);
        this.renderCard = this.renderCard.bind(this);

        this.timelineCategories = null;
        this.refCards = [];
    }

    componentDidMount() {
        const { productions } = this.props;
        if (productions === null) {
            this.fetchProductions();
        } else {
            TweenMax.set(this.refCards, {
                display: 'none',
                autoAlpha: 0,
            });
            this.timelineCategories = this.updateCategories();
        }
    }

    componentDidUpdate(prevProps) {
        const categoryChanged = prevProps.category !== this.props.category;
        if (categoryChanged) {
            this.timelineCategories = this.updateCategories();
        }

        const productionsChanged = prevProps.productions !== this.props.productions;
        if (productionsChanged && prevProps.productions === null) {
            TweenMax.set(this.refCards, {
                display: 'none',
                autoAlpha: 0,
            });
            this.timelineCategories = this.updateCategories();
        }
    }

    componentWillUnmount() {
        if (this.timelineCategories !== null) {
            this.timelineCategories.kill();
            this.timelineCategories = null;
        }
    }

    onProductionsLoaded(items) {
        this.props.setProductions(items);
    }

    fetchProductions() {
        const { urlGenerator } = this.props;
        fetch(urlGenerator.route('api.productions'))
            .then(response => response.json())
            .then(this.onProductionsLoaded);
    }

    updateCategories() {
        const { category, productions } = this.props;
        const timeline = new TimelineMax();
        if (productions === null) return timeline;

        const productionsWithRef = productions.map((it, index) => ({
            ref: this.refCards[index],
            production: it,
            hasCategory:
                category === null || get(it, 'data.categories', []).indexOf(category) !== -1,
        }));
        const hideElements = productionsWithRef
            .filter(({ hasCategory }) => !hasCategory)
            .map(({ ref }) => ref);
        const showElements = productionsWithRef
            .filter(({ hasCategory }) => hasCategory)
            .map(({ ref }) => ref);
        if (hideElements.length > 0) {
            timeline.staggerTo(
                hideElements,
                0.2,
                {
                    autoAlpha: 0,
                    onComplete: () => {
                        TweenMax.set(hideElements, {
                            display: 'none',
                        });
                    },
                },
                0.05,
                0,
            );
        }
        if (showElements.length > 0) {
            timeline.staggerTo(
                showElements,
                0.2,
                {
                    autoAlpha: 1,
                    onStart: () => {
                        TweenMax.set(showElements, {
                            display: 'block',
                        });
                    },
                },
                0.05,
                0,
            );
        }
        return timeline;
    }

    // eslint-disable-next-line class-methods-use-this
    renderCard(production, index) {
        return (
            <div
                key={`production-${production.id}`}
                className={styles.card}
                ref={(ref) => {
                    this.refCards[index] = ref;
                }}
            >
                <ProductionCard {...production} />
            </div>
        );
    }

    render() {
        const {
            intl, page, productions, categories, category,
        } = this.props;
        const { locale, formatMessage } = intl;
        const categoriesItems = [
            ...categories.map(cat => ({
                label: get(cat, `label.${locale}`, null),
                url: `${page.url}?category=${cat.id}`,
                current: `${category}` === `${cat.id}`,
            })),
            {
                label: formatMessage({
                    id: 'content.all',
                    defaultMessage: 'All',
                }),
                url: page.url,
                current: category === null,
            },
        ];

        return (
            <div className={styles.container}>
                <Helmet>
                    <title>
                        {intl.formatMessage(
                            {
                                id: 'meta.title_page',
                                defaultMessage: 'Accueil',
                            },
                            {
                                title: get(page, 'title', ''),
                            },
                        )}
                    </title>
                </Helmet>
                <div
                    className={classNames({
                        [styles.section]: true,
                        [styles.header]: true,
                    })}
                >
                    <div className={styles.cols}>
                        <div
                            className={classNames({
                                [styles.col]: true,
                                [styles.colTitle]: true,
                            })}
                        >
                            <h1 className={styles.title}>
                                {get(page, `data.title.${intl.locale}`, null)}
                            </h1>
                        </div>
                        <div
                            className={classNames({
                                [styles.col]: true,
                                [styles.colMenu]: true,
                            })}
                        >
                            <Menu
                                items={categoriesItems}
                                className={styles.menu}
                                itemClassName={styles.item}
                                currentClassName={styles.current}
                                linkClassName={styles.link}
                            />
                        </div>
                    </div>
                </div>
                <div
                    className={classNames({
                        [styles.section]: true,
                        [styles.list]: true,
                    })}
                >
                    {productions !== null ? productions.map(this.renderCard) : null}
                    {productions === null ? (
                        <div className={styles.loading}>
                            <PulseLoader color="#8d877b" />
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
}

Productions.propTypes = propTypes;
Productions.defaultProps = defaultProps;

const mapStateToProps = ({ site, layout }, { location }) => ({
    ...layout.size,
    page:
        site.page !== null && site.page.url === location.pathname
            ? site.page
            : site.pages.find(it => it.url === location.pathname) || null,
    productions: site.productions,
    categories: site.productionsCategories,
    category: get((location.search || '').match(/category=([^&]+)/), 1, null),
});
const mapDispatchToProps = dispatch => ({
    setProductions: items => dispatch(setProductionsActions(items)),
});
const WithStateContainer = connect(mapStateToProps, mapDispatchToProps)(Productions);
const WithUrlGeneratorContainer = withUrlGenerator()(WithStateContainer);
const WithIntlContainer = injectIntl(WithUrlGeneratorContainer);
const WithRouterContainer = withRouter(WithIntlContainer);

export default WithRouterContainer;



// WEBPACK FOOTER //
// ./js/components/pages/Productions.jsx